// THE STANDARD (DGC) - App Router
// Auth: email link or password, session via tRPC. Role-based routing: coach/admin → Command Center, client → The Trench
// Coach can preview client experience via role toggle

import { Route, Switch, Redirect, useLocation } from 'wouter';
import { Suspense, useEffect, type ReactNode } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from './contexts/ThemeContext';
import { trpc } from '@/lib/trpc';
import { getLoginUrl } from '@/const';
import ErrorBoundary from './components/ErrorBoundary';
import { PageErrorBoundary } from './components/PageErrorBoundary';
import { lazyRoute } from '@/lib/lazyRoute';
import { setDisplayTimezone } from '@/lib/timezone';
import { detectBrowserTimezone } from '@/lib/timezones';
import { Capacitor } from '@capacitor/core';
import { mapClientLocationToCoach } from '@/config/standardRoutes';
import { SubscriptionGate } from '@/components/SubscriptionGate';

// Layouts
import CoachLayout from '@/components/layout/CoachLayout';
import ClientLayout from '@/components/layout/ClientLayout';

// Auth
import Login from '@/pages/auth/Login';
import ResetPassword from '@/pages/auth/ResetPassword';

// Lazy: all pages are route-split for a smaller initial bundle
const CommunicationScheduler = lazyRoute(() => import('@/pages/coach/CommunicationScheduler'));
const StaffCommunicationAudit = lazyRoute(() => import('@/pages/coach/StaffCommunicationAudit'));
const CoachDashboard = lazyRoute(() => import('@/pages/coach/Dashboard'));
const CoachReviewCommand = lazyRoute(() => import('@/pages/coach/ReviewCommand'));
const CoachReliabilityControl = lazyRoute(() => import('@/pages/coach/ReliabilityControl'));
const ClientToday = lazyRoute(() => import('@/pages/client/Today'));


// Lazy: everything else is route-split for a smaller initial bundle
const CoachClients = lazyRoute(() => import('@/pages/coach/Clients'));
const ClientAudit = lazyRoute(() => import('@/pages/coach/ClientAudit'));
const CoachCopilot = lazyRoute(() => import('@/pages/coach/Copilot'));
const CoachClientProfile = lazyRoute(() => import('@/pages/coach/clientProfile/index.tsx'));
const CoachClientFormCheck = lazyRoute(() => import('@/pages/coach/clientFormCheck'));
const CoachClientBody = lazyRoute(() => import('@/pages/coach/ClientBody'));
const CoachClientProgress = lazyRoute(() => import('@/pages/coach/ClientProgress'));
const CoachClientProgram = lazyRoute(() => import('@/pages/coach/clientProgram/index.tsx'));
const ProgressHub = lazyRoute(() => import('@/pages/client/ProgressHub'));
const Reminders = lazyRoute(() => import('@/pages/Reminders'));
const CoachForge = lazyRoute(() => import('@/pages/coach/Forge'));
const CoachInbox = lazyRoute(() => import('@/pages/coach/Inbox'));
const CoachSettings = lazyRoute(() => import('@/pages/coach/Settings'));
const CoachPrograms = lazyRoute(() => import('@/pages/coach/Programs'));
const CoachProgramLevelStudio = lazyRoute(() => import('@/pages/coach/ProgramLevelStudio'));
const CoachSystem = lazyRoute(() => import('@/pages/coach/CoachSystem'));
const WeeklyReview = lazyRoute(() => import('@/pages/coach/WeeklyReview'));
const BrotherhoodBoard = lazyRoute(() => import('@/pages/coach/BrotherhoodBoard'));
const CustomExerciseLibrary = lazyRoute(() => import('@/pages/coach/CustomExerciseLibrary'));
const WorkoutLibrary = lazyRoute(() => import('@/pages/coach/WorkoutLibrary'));
const CoachWeeklyReports = lazyRoute(() => import('@/pages/coach/WeeklyReports'));
const CoachMarketplace = lazyRoute(() => import('@/pages/coach/Marketplace'));
const CoachFaithHub = lazyRoute(() => import('@/pages/coach/FaithHub'));
const CoachApplications = lazyRoute(() => import('@/pages/coach/Applications'));
const CoachSchedule = lazyRoute(() => import('@/pages/coach/Schedule'));
const CoachStudioFloor = lazyRoute(() => import('@/pages/coach/StudioFloor'));
const CoachWeeklyAccountability = lazyRoute(() => import('@/pages/coach/WeeklyAccountability'));
const CoachAnalytics = lazyRoute(() => import('@/pages/coach/Analytics'));
const CoachPayments = lazyRoute(() => import('@/pages/coach/Payments'));
const CoachChallenges = lazyRoute(() => import('@/pages/coach/Challenges'));
const CoachHabitHub = lazyRoute(() => import('@/pages/coach/HabitHub'));
const CoachPlaylistGenerator = lazyRoute(() => import('@/pages/coach/PlaylistGenerator'));
const CoachChallengeProgramDetail = lazyRoute(() => import('@/pages/coach/ChallengeProgramDetail'));
const CoachJournalReview = lazyRoute(() => import('@/pages/coach/JournalReview'));
const CoachStandardMemberships = lazyRoute(() => import('@/pages/coach/StandardMemberships'));

const ClientTraining = lazyRoute(() => import('@/pages/client/Training'));
const YourWeek = lazyRoute(() => import('@/pages/client/YourWeek'));
const ClientPlan = lazyRoute(() => import('@/pages/client/Plan'));
const ClientExerciseLibrary = lazyRoute(() => import('@/pages/client/ExerciseLibrary'));
const ClientNutrition = lazyRoute(() => import('@/pages/client/Nutrition'));
const ClientBody = lazyRoute(() => import('@/pages/client/Body'));
const ClientComms = lazyRoute(() => import('@/pages/client/Comms'));
const ClientOnboarding = lazyRoute(() => import('@/pages/client/Onboarding'));
const ClientOnboardingComplete = lazyRoute(() => import('@/pages/client/OnboardingComplete'));
// Phase 2g — value-first onboarding flow (top-level, no ClientLayout chrome)
const OnboardingFlow = lazyRoute(() => import('@/pages/onboarding'));
const ClientProgress = lazyRoute(() => import('@/pages/client/Progress'));
const ClientMeasurements = lazyRoute(() => import('@/pages/client/Measurements'));
const ClientPrograms = lazyRoute(() => import('@/pages/client/Programs'));
const ClientLibrary = lazyRoute(() => import('@/pages/client/Library'));
const VaultBrowse = lazyRoute(() => import('@/pages/client/library/VaultBrowse'));
const VaultDetail = lazyRoute(() => import('@/pages/client/library/VaultDetail'));
const CoachVault = lazyRoute(() => import('@/pages/coach/Vault'));
const VaultUpload = lazyRoute(() => import('@/pages/coach/VaultUpload'));
const CoachCourses = lazyRoute(() => import('@/pages/coach/Courses'));
const CourseEditor = lazyRoute(() => import('@/pages/coach/CourseEditor'));
const ClientCourses = lazyRoute(() => import('@/pages/client/Courses'));
const ClientCourseDetail = lazyRoute(() => import('@/pages/client/CourseDetail'));
const ClientLessonViewer = lazyRoute(() => import('@/pages/client/LessonViewer'));
const WorkoutHistory = lazyRoute(() => import('@/pages/client/WorkoutHistory'));
const HealthHistory = lazyRoute(() => import('@/pages/client/HealthHistory'));
const RecoveryCenter = lazyRoute(() => import('@/pages/client/RecoveryCenter'));
const ClientHabits = lazyRoute(() => import('@/pages/client/Habits'));
const ClientBilling = lazyRoute(() => import('@/pages/client/Billing'));
const ClientFormCheck = lazyRoute(() => import('@/pages/client/FormCheck'));
const ClientStandardScore = lazyRoute(() => import('@/pages/client/StandardScore'));
const ClientLedger = lazyRoute(() => import('@/pages/client/Ledger'));
const ClientContract = lazyRoute(() => import('@/pages/client/Contract'));
const ClientFilmSession = lazyRoute(() => import('@/pages/client/FilmSession'));
const MemberProfile = lazyRoute(() => import('@/pages/MemberProfile'));
const MemberDirectory = lazyRoute(() => import('@/pages/MemberDirectory'));
const Feed = lazyRoute(() => import('@/pages/Feed'));
const ClientWeeklyReports = lazyRoute(() => import('@/pages/client/WeeklyReports'));
const ClientWins = lazyRoute(() => import('@/pages/client/Wins'));
const ClientChallenges = lazyRoute(() => import('@/pages/client/Challenges'));
const ClientChallengeDetail = lazyRoute(() => import('@/pages/client/ChallengeDetail'));
const ClientChallengeHub = lazyRoute(() => import('@/pages/client/ChallengeHub'));
const ClientSchedule = lazyRoute(() => import('@/pages/client/Schedule'));
const ClientTasks = lazyRoute(() => import('@/pages/client/Tasks'));
const AccountSettings = lazyRoute(() => import('@/pages/client/AccountSettings'));
const StandardActivation = lazyRoute(() => import('@/pages/client/StandardActivation'));
const StandardStart = lazyRoute(() => import('@/pages/client/StandardStart'));

const Community = lazyRoute(() => import('@/pages/Community'));
const GroupDetail = lazyRoute(() => import('@/pages/GroupDetail'));
const GroupSettings = lazyRoute(() => import('@/pages/GroupSettings'));
const GroupJoin = lazyRoute(() => import('@/pages/GroupJoin'));
const Marketplace = lazyRoute(() => import('@/pages/Marketplace'));
const WeeklyCheckIn = lazyRoute(() => import('@/pages/WeeklyCheckIn'));
const ReferralLeaderboard = lazyRoute(() => import('@/pages/ReferralLeaderboard'));

const Apply = lazyRoute(() => import('@/pages/Apply'));
const Waitlist = lazyRoute(() => import('@/pages/Waitlist'));
const BookACall = lazyRoute(() => import('@/pages/BookACall'));
const Membership = lazyRoute(() => import('@/pages/Membership'));

const JournalBooks = lazyRoute(() => import('@/pages/client/JournalBooks'));
const JournalHome = lazyRoute(() => import('@/pages/client/JournalHome'));
const JournalWrite = lazyRoute(() => import('@/pages/client/JournalWrite'));
const JournalEntries = lazyRoute(() => import('@/pages/client/JournalEntries'));
const JournalEntry = lazyRoute(() => import('@/pages/client/JournalEntry'));
const TagBrowse = lazyRoute(() => import('@/pages/client/TagBrowse'));
const DevotionalReader = lazyRoute(() => import('@/pages/client/DevotionalReader'));
const DevotionalHistory = lazyRoute(() => import('@/pages/client/DevotionalHistory'));
const DivineMandateHub = lazyRoute(() => import('@/pages/client/DivineMandateHub'));
const FaithLibraryRoute = lazyRoute(() => import('@/pages/client/FaithLibraryPages'));
const BibleReader = lazyRoute(() => import('@/pages/client/BibleReader'));
const CoachPlaylists = lazyRoute(() => import('@/pages/client/CoachPlaylists'));
const WordStudy = lazyRoute(() => import('@/pages/client/WordStudy'));
const SharedEntry = lazyRoute(() => import('@/pages/public/SharedEntry'));
const SharedBook = lazyRoute(() => import('@/pages/public/SharedBook'));
const Privacy = lazyRoute(() => import('@/pages/public/Privacy'));
const Terms = lazyRoute(() => import('@/pages/public/Terms'));
const Blog = lazyRoute(() => import('@/pages/Blog'));
const BlogPost = lazyRoute(() => import('@/pages/BlogPost'));

import NotFound from '@/pages/NotFound';
import AcceptInvite from '@/pages/AcceptInvite';
import Landing from '@/pages/Landing';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { UpdateBanner } from '@/components/UpdateBanner';
import ReleaseNotesModal from '@/components/ReleaseNotesModal';
import { GuidedTourProvider } from '@/components/GuidedTour';
const Results = lazyRoute(() => import('@/pages/Results'));
// Keep the local component lab out of the production module graph. A normal
// dynamic import is still discovered and bundled by Vite even behind a DEV
// runtime guard, which adds the showcase's entire dependency tree to builds.
const componentShowcasePath = '/src/pages/ComponentShowcase.tsx';
const ComponentShowcase = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ componentShowcasePath))
  : null;
const socialPrototypeShowcasePath = '/src/pages/SocialPrototypeShowcase.tsx';
const SocialPrototypeShowcase = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ socialPrototypeShowcasePath))
  : null;
const dashboardPrototypePreviewPath = '/src/pages/DashboardPrototypePreview.tsx';
const DashboardPrototypePreview = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ dashboardPrototypePreviewPath))
  : null;
const coachStudioPrototypePath = '/src/pages/CoachStudioPrototype.tsx';
const CoachStudioPrototype = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ coachStudioPrototypePath))
  : null;
const programStudioPrototypePath = '/src/pages/ProgramStudioPrototype.tsx';
const ProgramStudioPrototype = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ programStudioPrototypePath))
  : null;
const dmConversationPrototypePath = '/src/pages/DmConversationPrototype.tsx';
const DmConversationPrototype = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ dmConversationPrototypePath))
  : null;
const nutritionLoggingPrototypePath = '/src/pages/NutritionLoggingPrototype.tsx';
const NutritionLoggingPrototype = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ nutritionLoggingPrototypePath))
  : null;
const ipadDesktopPreviewPath = '/src/dev/IpadDesktopPreview.tsx';
const IpadDesktopPreview = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ ipadDesktopPreviewPath))
  : null;
const faithLibraryPreviewPath = '/src/dev/FaithLibraryPreview.tsx';
const FaithLibraryPreview = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ faithLibraryPreviewPath))
  : null;
const slimLiveShotPath = '/src/dev/SlimLiveShot.tsx';
const SlimLiveShot = import.meta.env.DEV
  ? lazyRoute(() => import(/* @vite-ignore */ slimLiveShotPath))
  : null;

// Public paths that bypass auth guard
const PUBLIC_PATHS = ['/', '/login', '/auth/reset', '/marketplace', '/apply', '/waitlist', '/results', '/blog', '/book-a-call', '/membership', '/privacy', '/terms'];

function AuthGuard({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const isDevPreview = import.meta.env.DEV && location.startsWith('/__dev/');
  const { data: user, isLoading, isError, refetch } = trpc.auth.me.useQuery(undefined, {
    enabled: !isDevPreview,
  });

  // Local component labs are deliberately self-contained. Skipping the auth
  // request lets design reviews run from Vite alone, without booting the API,
  // database self-heals, or scheduled background jobs.
  if (isDevPreview) return <>{children}</>;

  // While the session check is loading — OR it failed transiently (server
  // redeploying / offline) with no cached answer — hold on a calm screen and
  // keep retrying. Never bounce a still-valid session to login on a blip; only
  // a definitive "no user" answer (below) logs someone out.
  if (isLoading || (isError && user === undefined)) {
    // Public enrollment information remains readable during an auth outage.
    // Protected routes still wait for a definitive session result.
    if (PUBLIC_PATHS.includes(location)) return <>{children}</>;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 fade-in">
        <div className="w-12 h-12 rounded-2xl bg-surface-2 skeleton-shimmer" />
        {isError ? (
          <>
            <p className="text-sm text-muted-foreground">Reconnecting…</p>
            <button
              onClick={() => refetch()}
              className="text-xs uppercase tracking-widest px-4 py-2 rounded-lg"
              style={{ background: 'var(--surface-2)', color: 'var(--text-secondary)', border: '1px solid var(--hairline)', fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Retry
            </button>
          </>
        ) : (
          <div className="h-3 w-24 rounded-lg bg-surface-1 skeleton-shimmer" />
        )}
      </div>
    );
  }

  if (!user && !isDevPreview && !PUBLIC_PATHS.some(p => location === p || location.startsWith('/invite') || location.startsWith('/groups/join') || location.startsWith('/apply') || location.startsWith('/waitlist') || location.startsWith('/shared-entry') || location.startsWith('/shared-book') || location.startsWith('/blog') || location.startsWith('/book-a-call') || location.startsWith('/privacy') || location.startsWith('/terms'))) {
    return <Redirect to="/" />;
  }

  if (user && location === '/login') {
    const pendingGroupInvite = sessionStorage.getItem('pending_group_invite');
    if (pendingGroupInvite) return <Redirect to={pendingGroupInvite} />;
    const isCoach = user.role === 'admin' || user.role === 'coach';
    return <Redirect to={isCoach ? '/coach/my/today' : '/client'} />;
  }

  return <>{children}</>;
}

function RouteFallback() {
  return (
    <div className="min-h-[60dvh] space-y-5 bg-[#f4f1eb] p-4 pt-5 fade-in md:p-6" role="status" aria-label="Loading page">
      <section className="relative overflow-hidden rounded-[26px] border border-[#74602f] bg-[#1d1d19] px-6 py-7 shadow-[0_18px_42px_rgba(29,29,25,0.12)]">
        <div className="pointer-events-none absolute -right-12 -top-16 h-52 w-52 rounded-full border border-[#c9a75d]/20" />
        <div className="relative space-y-3">
          <div className="h-3 w-28 rounded-full bg-[#c9a75d]/35 skeleton-shimmer" />
          <div className="h-9 w-56 max-w-[70%] rounded-lg bg-white/15 skeleton-shimmer" />
          <div className="h-4 w-full max-w-md rounded-full bg-white/10 skeleton-shimmer" />
        </div>
      </section>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <div key={item} className="h-28 rounded-2xl border border-[#d5d0c6] bg-[#fffdfa] skeleton-shimmer" />)}
      </div>
      <div className="space-y-3 rounded-[24px] border border-[#d5d0c6] bg-[#fffdfa] p-5 shadow-[0_12px_30px_rgba(29,29,25,0.05)]">
        <div className="h-4 w-40 rounded-full bg-[#e4e0d8] skeleton-shimmer" />
        <div className="h-20 rounded-2xl bg-[#efede7] skeleton-shimmer" />
        <div className="h-20 rounded-2xl bg-[#efede7] skeleton-shimmer" />
      </div>
      <span className="sr-only">Preparing the next screen</span>
    </div>
  );
}

// Uses the explicit route registry so coach deep links land in the correct
// coach or coach-as-athlete shell instead of assuming every client path has a
// same-suffix /coach/my route.
function CoachClientRedirect() {
  const [location] = useLocation();
  return <Redirect to={mapClientLocationToCoach(location)} />;
}

// ── Onboarding Gate (synchronous) ─────────────────────────────────────────────
// Prevents heavy client pages from mounting before the user has completed
// onboarding. Unlike the useEffect-based redirect in ClientLayout, this
// renders <Redirect> synchronously so ClientToday's 30+ hooks never fire.
function OnboardingGate({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const isAllowedPath = location.startsWith('/client/onboarding') || location.startsWith('/client/billing') || location.startsWith('/client/account') || location.startsWith('/client/standard-');
  const { data: user, isLoading } = trpc.auth.me.useQuery();
  const { data: scopedAccess, isLoading: scopedLoading } = trpc.community.getMyScopedAccess.useQuery(undefined, {
    enabled: !isAllowedPath && Boolean(user) && user?.role !== 'admin' && user?.role !== 'coach',
    retry: false,
  });
  const { data: standardState, isLoading: standardLoading } = trpc.standardMembership.getMyState.useQuery(undefined, {
    enabled: !isAllowedPath && Boolean(user) && user?.role !== 'admin' && user?.role !== 'coach',
    retry: false,
  });
  const isCoach = user?.role === 'admin' || user?.role === 'coach';
  const devBypassOnboarding =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    (() => {
      const enabled = new URLSearchParams(window.location.search).get('devBypassOnboarding') === '1';
      if (enabled) window.localStorage.setItem('ts-dev-bypass-onboarding', '1');
      return enabled || window.localStorage.getItem('ts-dev-bypass-onboarding') === '1';
    })();

  // Still loading — show nothing (Suspense fallback is already up)
  if (isLoading || scopedLoading || standardLoading || !user) return null;

  // Local QA only: lets us inspect client screens without mutating a test
  // account's onboarding state. Vite strips this from production behavior.
  if (devBypassOnboarding) return <>{children}</>;

  // Coaches skip onboarding entirely
  if (isCoach) return <>{children}</>;

  // A scoped group invite is intentionally a value-first, limited experience.
  // It must not force a trial participant through the paid-client intake.
  if (scopedAccess?.active) return <>{children}</>;

  // Allow legacy + billing paths through (legacy onboarding stays mounted as
  // fallback per Phase 2g merge plan; coach.resetClientOnboarding can still
  // route a client back to /client/onboarding for the full intake form).
  if (isAllowedPath) return <>{children}</>;

  if (standardState && !user.onboardingCompletedAt) {
    return <Redirect to="/client/onboarding" />;
  }

  // If onboarding isn't complete, redirect to the Phase 2g value-first flow.
  if (!user.onboardingCompletedAt) {
    return <Redirect to="/onboarding/identity" />;
  }

  return <>{children}</>;
}

function CoachRoutes() {
  const [location] = useLocation();
  const { data: user } = trpc.auth.me.useQuery();
  if (user?.isStaffAssistant) return <Suspense fallback={<RouteFallback />}><CommunicationScheduler standalone /></Suspense>;
  return (
    <CoachLayout>
      <Suspense fallback={<RouteFallback />}>
      <PageErrorBoundary resetKey={location}>
      <Switch>
        <Route path="/coach/communications" component={CommunicationScheduler} />
        <Route path="/coach/settings/audit" component={StaffCommunicationAudit} />
        <Route path="/coach" component={CoachDashboard} />
        <Route path="/coach/review-command" component={CoachReviewCommand} />
        <Route path="/coach/reliability" component={CoachReliabilityControl} />
        <Route path="/coach/standard-memberships" component={CoachStandardMemberships} />
        <Route path="/coach/clients" component={CoachClients} />
        <Route path="/coach/clients/audit" component={ClientAudit} />
        <Route path="/coach/clients/:clientId/form-check/:formCheckId?" component={CoachClientFormCheck} />
        <Route path="/coach/clients/:id/body" component={CoachClientBody} />
        <Route path="/coach/clients/:id/progress" component={CoachClientProgress} />
        <Route path="/coach/clients/:id/program" component={CoachClientProgram} />
        <Route path="/coach/clients/:id/:tab" component={CoachClientProfile} />
        <Route path="/coach/clients/:id" component={CoachClientProfile} />
        <Route path="/coach/members/:userId" component={MemberProfile} />
        <Route path="/coach/members" component={MemberDirectory} />
        <Route path="/coach/copilot" component={CoachCopilot} />
        <Route path="/coach/forge" component={CoachForge} />
        <Route path="/coach/blueprint">{() => <Redirect to="/coach/forge?tab=system" />}</Route>
        <Route path="/coach/schedule" component={CoachSchedule} />
        <Route path="/coach/floor" component={CoachStudioFloor} />
        <Route path="/coach/programs/levels" component={CoachProgramLevelStudio} />
        <Route path="/coach/system" component={CoachSystem} />
        <Route path="/coach/programs" component={CoachPrograms} />
        <Route path="/coach/community" component={Community} />
        <Route path="/community/groups/:id" component={GroupDetail} />
        <Route path="/community/groups/:id/settings" component={GroupSettings} />
        <Route path="/coach/inbox" component={CoachInbox} />
        <Route path="/coach/reminders">{() => <Reminders />}</Route>
        <Route path="/coach/settings" component={CoachSettings} />
        <Route path="/coach/weekly-review" component={WeeklyReview} />
        <Route path="/coach/brotherhood-board" component={BrotherhoodBoard} />
        <Route path="/coach/exercise-library" component={CustomExerciseLibrary} />
        <Route path="/coach/workout-library" component={WorkoutLibrary} />
        <Route path="/coach/weekly-reports" component={CoachWeeklyReports} />
        <Route path="/coach/marketplace" component={CoachMarketplace} />
        <Route path="/coach/faith" component={CoachFaithHub} />
        <Route path="/coach/applications" component={CoachApplications} />
        <Route path="/coach/accountability" component={CoachWeeklyAccountability} />
        <Route path="/coach/journal-review" component={CoachJournalReview} />
        <Route path="/coach/analytics" component={CoachAnalytics} />
        <Route path="/coach/payments" component={CoachPayments} />
        <Route path="/coach/challenges"><CoachChallenges /></Route>
        <Route path="/coach/challenge-programs/:id" component={CoachChallengeProgramDetail} />
        <Route path="/coach/habits" component={CoachHabitHub} />
        <Route path="/coach/playlist-generator" component={CoachPlaylistGenerator} />
        {/* My Training — coach trains himself using client pages inside CoachLayout */}
        <Route path="/coach/my/today" component={ClientToday} />
        <Route path="/coach/my/plan" component={ClientPlan} />
        <Route path="/coach/my/training" component={ClientTraining} />
        {/* Coach goes through their own courses as a student (client course pages). */}
        <Route path="/coach/my/courses/:slug/:lessonId" component={ClientLessonViewer} />
        <Route path="/coach/my/courses/:slug" component={ClientCourseDetail} />
        <Route path="/coach/my/courses" component={ClientCourses} />
        <Route path="/coach/my/library/vault/:slug" component={VaultDetail} />
        <Route path="/coach/my/library/vault" component={VaultBrowse} />
        <Route path="/coach/my/exercise-library" component={ClientExerciseLibrary} />
        <Route path="/coach/my/nutrition" component={ClientNutrition} />
        <Route path="/coach/my/schedule" component={ClientSchedule} />
        <Route path="/coach/my/habits" component={ClientHabits} />
        <Route path="/coach/my/tasks" component={ClientTasks} />
        <Route path="/coach/my/check-in" component={WeeklyCheckIn} />
        <Route path="/coach/my/body" component={ClientBody} />
        <Route path="/coach/my/progress" component={ClientProgress} />
        <Route path="/coach/my/measurements" component={ClientMeasurements} />
        <Route path="/coach/my/faith/:slug/day/:day" component={FaithLibraryRoute} />
        <Route path="/coach/my/faith/:slug/finish" component={FaithLibraryRoute} />
        <Route path="/coach/my/faith/:slug" component={FaithLibraryRoute} />
        <Route path="/coach/my/faith" component={DivineMandateHub} />
        <Route path="/coach/my/journal/write" component={JournalWrite} />
        <Route path="/coach/my/journal/entries" component={JournalEntries} />
        <Route path="/coach/my/journal/entry/:id" component={JournalEntry} />
        <Route path="/coach/my/journal" component={JournalHome} />
        <Route path="/coach/my/word-study" component={WordStudy} />
        <Route path="/coach/my/form-check" component={ClientFormCheck} />
        <Route path="/coach/my/standard-score" component={ClientStandardScore} />
        <Route path="/coach/my/history" component={WorkoutHistory} />
        <Route path="/coach/my/health" component={HealthHistory} />
        <Route path="/coach/my/recovery" component={RecoveryCenter} />
        <Route path="/coach/wins" component={ClientWins} />
        <Route path="/coach/my/reel">{() => <Redirect to="/coach/wins" />}</Route>
        <Route path="/coach/vault/new" component={VaultUpload} />
        <Route path="/coach/vault/:slug/edit" component={VaultUpload} />
        <Route path="/coach/vault" component={CoachVault} />
        <Route path="/coach/courses/:id/edit" component={CourseEditor} />
        <Route path="/coach/courses" component={CoachCourses} />
        <Route path="/coach/my/ledger" component={ClientLedger} />
        <Route path="/coach/my/week" component={YourWeek} />
        <Route path="/coach/my/contract" component={ClientContract} />
        <Route path="/coach/my/community" component={Community} />
        <Route path="/coach/my/challenges/:id" component={ClientChallengeDetail} />
        <Route path="/coach/my/challenges">{() => <ClientChallenges />}</Route>
        <Route path="/coach/my/challenge-program/:id" component={ClientChallengeHub} />
        <Route path="/coach/my/members/:userId" component={MemberProfile} />
        <Route path="/coach/my/members" component={MemberDirectory} />
        <Route path="/coach/my/billing" component={ClientBilling} />
        <Route path="/coach/my/account" component={AccountSettings} />
        <Route path="/coach/my/film-session/:id" component={ClientFilmSession} />
        <Route path="/coach/my/programs" component={ClientPrograms} />
        <Route path="/coach/my/feed" component={Feed} />
        <Route path="/coach/my/divine-mandate" component={DivineMandateHub} />
        <Route path="/coach/my/journal/books" component={JournalBooks} />
        <Route path="/coach/my/bible" component={BibleReader} />
        <Route path="/coach/my/devotional/reader" component={DevotionalReader} />
        <Route path="/coach/my/devotional/history" component={DevotionalHistory} />
        <Route path="/coach/my/devotional" component={DivineMandateHub} />
        <Route path="/coach/my/coach-playlists" component={CoachPlaylists} />
        {/* Catchall inside CoachRoutes: stale push notification URLs land on
            the coach dashboard instead of a dead-end 404. */}
        <Route><Redirect to="/coach" /></Route>
      </Switch>
      </PageErrorBoundary>
      </Suspense>
    </CoachLayout>
  );
}

function ClientRoutes() {
  const [location] = useLocation();
  return (
    <ClientLayout>
      <OnboardingGate>
      <Suspense fallback={<RouteFallback />}>
      <PageErrorBoundary resetKey={location}>
      <Switch>
        <Route path="/client/standard-activation" component={StandardActivation} />
        <Route path="/client/standard-start" component={StandardStart} />
        <Route path="/client" component={ClientToday} />
        <Route path="/client/week" component={YourWeek} />
        <Route path="/client/plan" component={ClientPlan} />
        <Route path="/client/training" component={ClientTraining} />
        <Route path="/client/exercise-library" component={ClientExerciseLibrary} />
        <Route path="/client/programs" component={ClientPrograms} />
        <Route path="/client/library" component={ClientLibrary} />
        {/* Phase 2c: @cookbook / @vault mention destinations (real Vault = Phase 3) */}
        <Route path="/client/library/cookbook" component={VaultBrowse} />
        <Route path="/client/library/vault/:slug" component={VaultDetail} />
        <Route path="/client/library/vault" component={VaultBrowse} />
        {/* Courses (Phase 1) — most specific route first */}
        <Route path="/client/courses/:slug/:lessonId" component={ClientLessonViewer} />
        <Route path="/client/courses/:slug" component={ClientCourseDetail} />
        <Route path="/client/courses" component={ClientCourses} />
        <Route path="/client/body" component={ClientBody} />
        <Route path="/client/nutrition" component={ClientNutrition} />
        <Route path="/client/schedule" component={ClientSchedule} />
        <Route path="/client/community" component={Community} />
        <Route path="/community/groups/:id" component={GroupDetail} />
        <Route path="/community/groups/:id/settings" component={GroupSettings} />
        <Route path="/client/comms" component={ClientComms} />
        <Route path="/client/onboarding/complete" component={ClientOnboardingComplete} />
        <Route path="/client/onboarding">{() => <ClientOnboarding />}</Route>
        <Route path="/client/progress">{() => <ProgressHub />}</Route>
        <Route path="/client/progress-legacy" component={ClientProgress} />
        <Route path="/client/measurements" component={ClientMeasurements} />
        <Route path="/client/history" component={WorkoutHistory} />
        <Route path="/client/health" component={HealthHistory} />
        <Route path="/client/recovery" component={RecoveryCenter} />
        <Route path="/client/habits" component={ClientHabits} />
        <Route path="/client/tasks" component={ClientTasks} />
        <Route path="/client/reminders">{() => <Reminders />}</Route>
        <Route path="/client/billing" component={ClientBilling} />
        <Route path="/client/form-check" component={ClientFormCheck} />
        <Route path="/client/standard-score" component={ClientStandardScore} />
        <Route path="/client/ledger" component={ClientLedger} />
        <Route path="/client/contract" component={ClientContract} />
        <Route path="/client/film-session/:id" component={ClientFilmSession} />
        <Route path="/client/members/:userId" component={MemberProfile} />
        <Route path="/client/members" component={MemberDirectory} />
        <Route path="/client/feed" component={Feed} />
        <Route path="/client/weekly-reports" component={ClientWeeklyReports} />
        <Route path="/client/wins" component={ClientWins} />
        <Route path="/client/reel">{() => <Redirect to="/client/wins" />}</Route>
        <Route path="/client/divine-mandate" component={DivineMandateHub} />
        <Route path="/client/devotional/reader" component={DevotionalReader} />
        <Route path="/client/devotional/history" component={DevotionalHistory} />
        <Route path="/client/bible/studio">{() => { if (typeof window !== 'undefined') window.location.replace('/client/bible?tab=library'); return null; }}</Route>
        <Route path="/client/bible" component={BibleReader} />
        <Route path="/client/reading-plans">{() => { if (typeof window !== 'undefined') window.location.replace('/client/faith'); return null; }}</Route>
        <Route path="/client/coach-playlists" component={CoachPlaylists} />
        <Route path="/client/word-study" component={WordStudy} />
        <Route path="/client/faith/:slug/day/:day" component={FaithLibraryRoute} />
        <Route path="/client/faith/:slug/finish" component={FaithLibraryRoute} />
        <Route path="/client/faith/:slug" component={FaithLibraryRoute} />
        <Route path="/client/faith" component={DivineMandateHub} />
        <Route path="/client/devotional" component={DivineMandateHub} />
        <Route path="/client/journal/books" component={JournalBooks} />
        <Route path="/client/journal/tags" component={TagBrowse} />
        <Route path="/client/journal/write" component={JournalWrite} />
        <Route path="/client/journal/entries" component={JournalEntries} />
        <Route path="/client/journal/entry/:id" component={JournalEntry} />
        <Route path="/client/journal" component={JournalHome} />
        <Route path="/client/challenges/:id" component={ClientChallengeDetail} />
        <Route path="/client/challenges">{() => <ClientChallenges />}</Route>
        <Route path="/client/challenge-program/:id" component={ClientChallengeHub} />
        <Route path="/client/check-in" component={WeeklyCheckIn} />
        <Route path="/client/referrals" component={ReferralLeaderboard} />
        <Route path="/client/account" component={AccountSettings} />
        {/* Catchall inside ClientRoutes: stale push notification URLs land on
            the client home instead of a dead-end 404. */}
        <Route><Redirect to="/client" /></Route>
      </Switch>
      </PageErrorBoundary>
      </Suspense>
      </OnboardingGate>
    </ClientLayout>
  );
}

function DevOnboardingPreview() {
  const requestedStep = typeof window !== 'undefined'
    ? Number(new URLSearchParams(window.location.search).get('step') ?? 1)
    : 1;
  const previewStep = Number.isFinite(requestedStep) ? requestedStep : 1;
  return (
    // The live route sits under the app top bar, which clears the status bar.
    // The preview has no top bar, so it reserves the safe area itself.
    <div className="onboarding-layout-viewport overflow-hidden" style={{ height: 'calc(100dvh - 112px)', marginTop: 'env(safe-area-inset-top, 0px)' }}>
      <ClientOnboarding previewStep={previewStep} />
    </div>
  );
}

function DevContrastPreview() {
  return (
    <div className="premium-app-shell min-h-screen bg-[#171715] p-4 sm:p-8">
      <main className="premium-app-content mx-auto min-h-[calc(100dvh-2rem)] max-w-4xl overflow-hidden rounded-[28px] sm:min-h-[calc(100dvh-4rem)]">
        <section data-contrast="light" className="client-light-page premium-light-surface min-h-full bg-[#f2f0ec] p-5 text-[#17151a] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#2f5d73]">Contrast certification</p>
          <h1 data-testid="contrast-light-heading" className="mt-2 text-4xl font-black uppercase">Light Surface</h1>
          <p className="mt-2 max-w-xl text-sm text-[#4f5960]">Ink stays readable on the warm client canvas.</p>

          <section data-contrast="dark" className="premium-dark-surface mt-6 rounded-[24px] border border-white/15 bg-[#171715] p-5 text-white sm:p-7">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#d6b879]">Your next move</p>
            <h2 data-testid="contrast-dark-heading" className="mt-2 text-3xl font-black uppercase">Your Work Is Ready.</h2>
            <p data-testid="contrast-dark-muted" className="mt-2 text-sm text-zinc-500">Every heading, supporting line, and field remains readable on a dark surface.</p>
            <label className="mt-5 block text-xs font-black uppercase tracking-wider text-white" htmlFor="contrast-preview-input">Member note</label>
            <input id="contrast-preview-input" data-testid="contrast-dark-input" className="mt-2 w-full px-4" placeholder="Type a clear next step" />
            <button type="button" className="mt-4 min-h-12 rounded-xl bg-[#2f5d73] px-5 text-sm font-black uppercase tracking-wider text-white">Continue</button>
          </section>
        </section>
      </main>
    </div>
  );
}

function Router() {
  const { data: user } = trpc.auth.me.useQuery();
  const isCoach = user?.role === 'admin' || user?.role === 'coach';
  const isClient = user?.role === 'client' || user?.role === 'user';
  const isNativeApp = Capacitor.isNativePlatform();

  // Keep the global timezone formatter in step with the user's preference.
  // When the user hasn't saved one (or for unauthenticated views), fall back
  // to whatever the browser reports — that's the user's actual geolocation,
  // which is almost always what they want over a hardcoded Pacific default.
  useEffect(() => {
    const stored = (user as { timezone?: string | null } | null)?.timezone;
    setDisplayTimezone(stored && stored.length > 0 ? stored : detectBrowserTimezone());
  }, [user]);

  // After OAuth login, check if there's a pending invite token stored before login redirect
  const pendingInviteToken = typeof window !== 'undefined' ? sessionStorage.getItem('invite_token') : null;
  const pendingGroupInvite = typeof window !== 'undefined' ? sessionStorage.getItem('pending_group_invite') : null;

  return (
    <AuthGuard>
      <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/__dev/components">
          {import.meta.env.DEV && ComponentShowcase ? <ComponentShowcase /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/social-prototypes">
          {import.meta.env.DEV && SocialPrototypeShowcase ? <SocialPrototypeShowcase /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/onboarding-preview">
          {import.meta.env.DEV ? <DevOnboardingPreview /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/contrast-preview">
          {import.meta.env.DEV ? <DevContrastPreview /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/dashboard-prototype">
          {import.meta.env.DEV && DashboardPrototypePreview ? <DashboardPrototypePreview /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/coach-studio-prototype">
          {import.meta.env.DEV && CoachStudioPrototype ? <CoachStudioPrototype /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/program-studio-prototype">
          {import.meta.env.DEV && ProgramStudioPrototype ? <ProgramStudioPrototype /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/dm-conversation-prototype">
          {import.meta.env.DEV && DmConversationPrototype ? <DmConversationPrototype /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/nutrition-logging-prototype">
          {import.meta.env.DEV && NutritionLoggingPrototype ? <NutritionLoggingPrototype /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/membership">
          {import.meta.env.DEV ? <Membership /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/ipad-desktop">
          {import.meta.env.DEV && IpadDesktopPreview ? <IpadDesktopPreview /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/faith-library">
          {import.meta.env.DEV && FaithLibraryPreview ? <FaithLibraryPreview /> : <Redirect to="/login" />}
        </Route>
        <Route path="/__dev/slim-live">
          {import.meta.env.DEV && SlimLiveShot ? <SlimLiveShot /> : <Redirect to="/login" />}
        </Route>
        <Route path="/login" component={Login} />
        <Route path="/auth/reset" component={ResetPassword} />
        <Route path="/invite/:token" component={AcceptInvite} />
        <Route path="/groups/join/:slug/:token" component={GroupJoin} />
        <Route path="/apply" component={Apply} />
        <Route path="/waitlist" component={Waitlist} />
        <Route path="/book-a-call" component={BookACall} />
        <Route path="/membership" component={Membership} />
        <Route path="/marketplace" component={Marketplace} />
        <Route path="/results" component={Results} />
        <Route path="/shared-entry/:token" component={SharedEntry} />
        <Route path="/shared-book/:token" component={SharedBook} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        {/* Group links intentionally live outside /client and /coach so they can
            be shared. Route the authenticated user back through the correct
            portal shell; the nested switches own the actual detail screens. */}
        <Route path={/^\/community\/groups\/[^/]+(?:\/settings)?\/?$/}>
          {isCoach ? <CoachRoutes /> : isClient ? <ClientRoutes /> : <Redirect to="/login" />}
        </Route>
        <Route path={/^\/coach(\/.*)?$/}>
          {isCoach ? <CoachRoutes /> : <Redirect to="/login" />}
        </Route>
        {/* Phase 2g — value-first onboarding lives at /onboarding/* outside
            ClientLayout. The flow's own guard handles auth + re-onboarding. */}
        <Route path={/^\/onboarding(\/.*)?$/}>
          <SubscriptionGate><OnboardingFlow /></SubscriptionGate>
        </Route>
        {/* Client routes: coaches get redirected to /coach/my/* equivalents */}
        <Route path={/^\/client(\/.*)?$/}>
          {isCoach ? <CoachClientRedirect /> : isClient ? <ClientRoutes /> : <Redirect to="/login" />}
        </Route>
        <Route path="/">
          {user ? (
            // If there's a pending invite token from before login, complete the redemption first
            pendingGroupInvite ? (
              <Redirect to={pendingGroupInvite} />
            ) : pendingInviteToken ? (
              <Redirect to={`/invite/${pendingInviteToken}`} />
            ) : (
              <Redirect to={isCoach ? '/coach/my/today' : '/client'} />
            )
          ) : (
            isNativeApp ? <Redirect to="/login" /> : <Landing />
          )}
        </Route>
        <Route component={NotFound} />
      </Switch>
      </Suspense>
    </AuthGuard>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <GuidedTourProvider>
            <UpdateBanner />
            {/* Toasts start below the top bar (48px, or the 62px thread
                header) and the safe area, so an error never covers Back. */}
            <Toaster
              position="top-right"
              theme="dark"
              offset={{ top: 72, right: 24 }}
              mobileOffset={{
                top: 'calc(env(safe-area-inset-top, 0px) + 68px)',
                right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
                left: 'calc(env(safe-area-inset-left, 0px) + 12px)',
              }}
            />
            <Router />
            <PWAInstallPrompt />
            <ReleaseNotesModal />
          </GuidedTourProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
